import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { ifWeb } from "@/utils/platform";

import {
  formatCandidateGpr,
  formatCandidateMeta,
  formatCandidateName,
} from "../../../utils/addPlayersRowMeta";
import AddPlayersRow from "../../molecules/AddPlayersRow";

import type { SuggestedCandidate } from "../../../hooks/useSuggestedPlayers";

/** Props for {@link SuggestedPlayersListView}. */
interface SuggestedPlayersListViewProps {
  /**
   * Candidates in the order they must render — already sorted by `bestRank`
   * and already stripped of session-dismissed entries by the container.
   */
  readonly candidates: readonly SuggestedCandidate[];
  /** Whether the first page is loading. */
  readonly isLoading: boolean;
  /** Read error, or `null`. */
  readonly error: string | null;
  /** Player ids with an add in flight. */
  readonly addingPlayerIds: readonly string[];
  /** Add a candidate, by player id. */
  readonly onAdd: (playerId: string) => void;
  /**
   * Hide a candidate for this session, by its stable suggestion id. Fires no
   * network request.
   */
  readonly onDismiss: (suggestionId: string) => void;
}

/**
 * The Suggested tab's body.
 * @param root0 - Component props.
 * @param root0.candidates - Candidates, strongest first.
 * @param root0.isLoading - Whether the first page is loading.
 * @param root0.error - Read error.
 * @param root0.addingPlayerIds - Ids with an add in flight.
 * @param root0.onAdd - Add callback.
 * @param root0.onDismiss - Dismiss callback.
 */
const SuggestedPlayersListView = ({
  candidates,
  isLoading,
  error,
  addingPlayerIds,
  onAdd,
  onDismiss,
}: SuggestedPlayersListViewProps) =>
  isLoading ? (
    <Box className="flex-1 items-center justify-center p-4">
      <Spinner size="small" />
    </Box>
  ) : error ? (
    <Box className="flex-1 items-center justify-center p-4">
      <Text variant="caption" className="text-center text-status-error">
        {error}
      </Text>
    </Box>
  ) : (
    /* The artboard's content column is ONE column with a uniform 12px gap
       between the first card and every card after it. SE-6949 removed the
       in-list Reason dropdown that used to sit above the first card — reason
       narrowing now lives in the drawer's filters panel, whose committed
       selection routes to the filtered-results view instead. */
    <Stack className="min-h-0 flex-1 gap-3">
      {/* No horizontal padding here or on the card column below: the drawer
          shell already insets its content by 20px, which IS the design's
          content column. */}
      {candidates.length === 0 ? (
        <Box
          testID={TEST_ID_ADD_PLAYERS_PANEL.SUGGESTED_EMPTY}
          className="flex-1 items-center justify-center p-4"
        >
          <Text variant="caption" className="text-center text-content-muted">
            No suggestions right now. Use search to add any player.
          </Text>
        </Box>
      ) : (
        /* The list scrolls but shows NO scrollbar. On web the native track is
           laid out INSIDE the scroller, so it was stealing a strip of the card
           width and reading as extra right-hand margin — the drawer is only
           320px of content, so a ~15px track is visible dead space.
           `showsVerticalScrollIndicator` covers native; `scrollbar-hide`
           (global.css, webkit + standard) is the repo's existing way to do the
           same on web, and `ifWeb` keeps it off native. */
        <ScrollView
          testID={TEST_ID_ADD_PLAYERS_PANEL.SUGGESTED_LIST}
          className={`flex-1 ${ifWeb("scrollbar-hide", "")}`.trim()}
          showsVerticalScrollIndicator={false}
        >
          {/* No TOP padding — the column gap above already supplies the
              artboard's 12px step to the first card, and 8 more on top of it
              would make that one step 20. The bottom padding is scroll
              breathing room, not rhythm. */}
          <Stack className="gap-3 pb-2">
            {candidates.map(candidate => (
              <AddPlayersRow
                key={candidate.id}
                playerId={candidate.player.id}
                suggestionId={candidate.id}
                name={formatCandidateName(candidate.player)}
                meta={formatCandidateMeta(candidate.player)}
                gpr={formatCandidateGpr(candidate.player)}
                reasons={candidate.reasons}
                isAdding={addingPlayerIds.includes(candidate.player.id)}
                onAdd={onAdd}
                onDismiss={onDismiss}
              />
            ))}
          </Stack>
        </ScrollView>
      )}
    </Stack>
  );

SuggestedPlayersListView.displayName = "SuggestedPlayersListView";

export default memo(SuggestedPlayersListView);
